import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';

export interface TestimonialItem {
  quote: string;
  author: string;
  role?: string;
}

interface TestimonialSliderProps {
  items: TestimonialItem[];
  className?: string;
}

export const TestimonialSlider: React.FC<TestimonialSliderProps> = ({
  items,
  className,
}) => {
  const [index, setIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const go = (dir: -1 | 1) => {
    setIndex((i) => (i + dir + items.length) % items.length);
  };

  const minSwipe = 50;
  const onTouchStart = (e: React.TouchEvent) => setTouchStart(e.targetTouches[0].clientX);
  const onTouchMove = (e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (touchStart == null || touchEnd == null) return;
    const diff = touchStart - touchEnd;
    if (Math.abs(diff) >= minSwipe) {
      if (diff > 0) go(1);
      else go(-1);
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const interval = setInterval(() => setIndex((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(interval);
  }, [items.length]);

  const item = items[index];
  if (!item) return null;

  return (
    <div className={clsx('relative', className)}>
      <div
        ref={ref}
        className="overflow-hidden rounded-2xl border border-app-border dark:border-gray-700 bg-app-surface dark:bg-gray-800/80 p-8 sm:p-10"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <blockquote className="text-lg sm:text-xl text-app-text dark:text-gray-200 italic">
          &ldquo;{item.quote}&rdquo;
        </blockquote>
        <footer className="mt-6 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold">
            {item.author.charAt(0)}
          </div>
          <div>
            <cite className="not-italic font-semibold text-app-text dark:text-white">
              {item.author}
            </cite>
            {item.role && (
              <p className="text-sm text-app-text-muted dark:text-gray-400">{item.role}</p>
            )}
          </div>
        </footer>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => go(-1)}
          className="p-2 rounded-lg text-app-text-muted dark:text-gray-400 hover:bg-app-surface-muted dark:hover:bg-gray-800 hover:text-app-text dark:hover:text-white transition-colors"
          aria-label="Previous testimonial"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="flex gap-2">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              className={clsx(
                'h-2 rounded-full transition-all duration-300',
                i === index
                  ? 'w-6 bg-primary-500'
                  : 'w-2 bg-app-border dark:bg-gray-600 hover:bg-app-text-muted dark:hover:bg-gray-500'
              )}
              aria-label={`Go to testimonial ${i + 1}`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => go(1)}
          className="p-2 rounded-lg text-app-text-muted dark:text-gray-400 hover:bg-app-surface-muted dark:hover:bg-gray-800 hover:text-app-text dark:hover:text-white transition-colors"
          aria-label="Next testimonial"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};
